import React, { useState, useEffect, useCallback } from 'react';
import { getUserBaseline } from '../api/client';
import { RefreshButton, RefreshingBanner, LoadingCard, dataFadeClass } from './ui/LoadingState';
import Badge from './ui/Badge';
import { normalizeCountry } from '../utils/countryNorm';

// Defensive: PowerShell ConvertTo-Json collapses single-element arrays to a
// scalar, so any list field can arrive as a string/object instead of an array.
function toArr(v) {
  if (Array.isArray(v)) return v;
  if (v === null || v === undefined || v === '') return [];
  return [v];
}

function fmtHourRange(earliest, latest) {
  const valid = (h) => h !== null && h !== undefined && h !== '';
  if (!valid(earliest) && !valid(latest)) return '—';
  const pad = (h) => String(h).padStart(2, '0');
  if (valid(earliest) && valid(latest)) return `${pad(earliest)}:00–${pad(latest)}:59 UTC`;
  return `${pad(valid(earliest) ? earliest : latest)}:00 UTC`;
}

// "Usually Yes" / "Usually No" / "—" for the per-OS managed/compliant flags.
function BoolBadge({ value }) {
  if (value === true) return <Badge tone="success">Usually Yes</Badge>;
  if (value === false) return <Badge tone="warning">Usually No</Badge>;
  return <span className="text-gray-600">—</span>;
}

// One side of the comparison (7-day or 30-day) rendered as a labeled cell value.
function ListCell({ items, joiner = ', ', empty = '—', normalize }) {
  const arr = toArr(items).filter(Boolean);
  if (arr.length === 0) return <span className="text-gray-600 italic">{empty}</span>;
  const display = normalize ? arr.map(normalize) : arr;
  return <span className="text-gray-300">{display.join(joiner)}</span>;
}

// Collapsible "N known IPs" cell with an expandable list.
function KnownIpsCell({ items }) {
  const [open, setOpen] = useState(false);
  const arr = toArr(items).filter(Boolean);
  if (arr.length === 0) return <span className="text-gray-600 italic">—</span>;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-gray-300 hover:text-blue-400 transition-colors"
      >
        {arr.length} known IP{arr.length === 1 ? '' : 's'} {open ? '▾' : '▸'}
      </button>
      {open && (
        <div className="mt-1 space-y-0.5 font-mono text-xs text-gray-400 max-h-40 overflow-y-auto">
          {arr.map((ip) => (<div key={ip}>{ip}</div>))}
        </div>
      )}
    </div>
  );
}

const COL = 'px-3 py-2 text-left align-top';

export default function BaselineTab({ caseId, iocDate = '' }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);          // full API response
  const [snapshotDate, setSnapshotDate] = useState('');

  const load = useCallback(async (snap) => {
    if (!caseId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getUserBaseline(caseId, snap ? { snapshotDate: snap } : {});
      setData(res);
      setSnapshotDate(res?.snapshotDate || '');
    } catch (err) {
      setError(err?.message || 'Failed to load baseline.');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  // Load the IOC-anchored default on mount / case change.
  useEffect(() => { load(); }, [load]);

  const onPickSnapshot = (e) => {
    const d = e.target.value;
    setSnapshotDate(d);
    load(d);
  };

  const seven = data?.sevenDay || null;
  const thirty = data?.thirtyDay || null;
  const availableDates = toArr(data?.availableDates);
  // Dates arrive newest-first; the last entry is the earliest snapshot we hold.
  const earliestAvailable = availableDates.length ? availableDates[availableDates.length - 1] : null;
  const hasData = !!(seven || thirty);

  // Union of OS names across both windows, for the per-OS breakdown.
  const osNames = Array.from(new Set([
    ...toArr(seven?.osProfiles).map((p) => p.os),
    ...toArr(thirty?.osProfiles).map((p) => p.os),
  ])).filter(Boolean);
  const osLookup = (snap, os) => toArr(snap?.osProfiles).find((p) => p.os === os) || null;

  // Snapshot-level comparison rows.
  const rows = [
    { label: 'Countries', s: <ListCell items={seven?.countries} normalize={normalizeCountry} />, t: <ListCell items={thirty?.countries} normalize={normalizeCountry} /> },
    { label: 'Known IPs', s: <KnownIpsCell items={seven?.knownIps} />, t: <KnownIpsCell items={thirty?.knownIps} /> },
    { label: 'States', s: <ListCell items={seven?.states} />, t: <ListCell items={thirty?.states} /> },
    { label: 'Cities', s: <ListCell items={seven?.cities} />, t: <ListCell items={thirty?.cities} /> },
    { label: 'Typical apps', s: <ListCell items={seven?.apps} />, t: <ListCell items={thirty?.apps} /> },
  ];

  if (loading && !data) {
    return <LoadingCard message="Loading baseline…" />;
  }

  return (
    <div className="space-y-4">
      {/* Header: snapshot picker + window context */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-100">User Behavior Baseline</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            The investigated user's normal sign-in profile (7-day vs 30-day window).
            {iocDate && <> Default snapshot anchored to the IOC date <span className="text-gray-300">{iocDate}</span>.</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-400">Snapshot date</label>
          <select
            value={snapshotDate}
            onChange={onPickSnapshot}
            disabled={loading || availableDates.length === 0}
            className="bg-gray-800 text-gray-200 text-xs border border-gray-700 rounded px-2 py-1 focus:outline-none focus:border-blue-600"
          >
            {availableDates.length === 0 && <option value="">No snapshots</option>}
            {availableDates.map((d) => (
              <option key={d} value={d}>{d}{iocDate && d === iocDate ? ' (IOC)' : ''}</option>
            ))}
          </select>
          <RefreshButton loading={loading} onClick={() => load(snapshotDate)} />
        </div>
      </div>

      <RefreshingBanner show={loading && !!data} message="Loading baseline…" />

      {error && (
        <div className="bg-red-900/40 border border-red-700/50 rounded p-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {!error && !hasData && (
        <div className="card p-6 text-center text-gray-400 text-sm">
          No baseline data available for this user
          {snapshotDate ? <> on <span className="text-gray-300">{snapshotDate}</span></> : ''}.
          {availableDates.length > 0 && <> Try a different snapshot date.</>}
        </div>
      )}

      {!error && hasData && (
        <div className={dataFadeClass(loading)}>
          {/* Window dates */}
          <div className="flex flex-wrap gap-x-8 gap-y-1 text-xs text-gray-500 mb-3">
            {seven?.windowStart && (
              <span>7-day window: <span className="text-gray-300">{seven.windowStart} → {seven.windowEnd}</span></span>
            )}
            {thirty?.windowStart && (
              <span>30-day window: <span className="text-gray-300">{thirty.windowStart} → {thirty.windowEnd}</span></span>
            )}
          </div>

          {/* Snapshot-level comparison */}
          <div className="card overflow-hidden">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700 bg-gray-800/50">
                  <th className={`${COL} text-gray-400 font-medium w-40`}>Field</th>
                  <th className={`${COL} text-gray-400 font-medium`}>7-day baseline</th>
                  <th className={`${COL} text-gray-400 font-medium`}>30-day baseline</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.label} className="border-b border-gray-800 last:border-0">
                    <td className={`${COL} text-gray-400`}>{r.label}</td>
                    <td className={COL}>{r.s}</td>
                    <td className={COL}>{r.t}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Per-OS breakdown */}
          {osNames.length > 0 && (
            <div className="card overflow-hidden mt-4">
              <div className="px-3 py-2 border-b border-gray-700 bg-gray-800/50 text-gray-400 text-xs font-medium">
                Per-OS profile
              </div>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700 text-gray-500 text-xs">
                    <th className={COL}>OS</th>
                    <th className={COL} title="Earliest snapshot in which this OS appears for the user (derived from the baseline snapshot series).">First seen</th>
                    <th className={COL}>Window</th>
                    <th className={COL}>Sign-in hours</th>
                    <th className={COL}>Sign-ins</th>
                    <th className={COL}>Compliant</th>
                    <th className={COL}>Managed</th>
                  </tr>
                </thead>
                <tbody>
                  {osNames.flatMap((os) => {
                    const p7 = osLookup(seven, os);
                    const p30 = osLookup(thirty, os);
                    const firstSeen = p7?.firstSeen || p30?.firstSeen || null;
                    // "New" = appeared after our data began (not just the retention edge)
                    // AND within the currently-selected 30-day window.
                    const winStart = thirty?.windowStart || seven?.windowStart || '';
                    const isNew = !!(firstSeen && earliestAvailable && firstSeen > earliestAvailable && (!winStart || firstSeen >= winStart));
                    return [
                      ['7-day', p7],
                      ['30-day', p30],
                    ].map(([winLabel, p]) => (
                      <tr key={`${os}-${winLabel}`} className="border-b border-gray-800 last:border-0">
                        <td className={`${COL} text-gray-300`}>{winLabel === '7-day' ? os : ''}</td>
                        <td className={COL}>
                          {winLabel === '7-day'
                            ? (firstSeen
                                ? (<span className="text-gray-300">{firstSeen}{isNew && <span className="ml-1.5"><Badge tone="warning">new in window</Badge></span>}</span>)
                                : <span className="text-gray-600">—</span>)
                            : ''}
                        </td>
                        <td className={`${COL} text-gray-500`}>{winLabel}</td>
                        <td className={`${COL} text-gray-300`}>{p ? fmtHourRange(p.earliestHour, p.latestHour) : '—'}</td>
                        <td className={`${COL} text-gray-300`}>{p ? p.signinCount : '—'}</td>
                        <td className={COL}>{p ? <BoolBadge value={p.normallyCompliant} /> : <span className="text-gray-600">—</span>}</td>
                        <td className={COL}>{p ? <BoolBadge value={p.normallyManaged} /> : <span className="text-gray-600">—</span>}</td>
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
              <div className="px-3 py-2 text-[11px] text-gray-600 border-t border-gray-800">
                "First seen" is the earliest baseline snapshot in which the OS appears for this user — it's window-bounded (a snapshot reflects activity back across its 7-/30-day window), so it's an approximate first-appearance, not the exact first sign-in.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
